import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./DashboardHome.css";

// ── Tool definitions ───────────────────────────────────────────────
const PDF_CONVERT = [
  { emoji: "📝", title: "Word to PDF",       desc: "DOC & DOCX",  path: "/dashboard/word2pdf",  color: "#2b5ce6" },
  { emoji: "📊", title: "Excel to PDF",      desc: "XLS & XLSX",  path: "/dashboard/excel2pdf", color: "#1a7a43" },
  { emoji: "📑", title: "PowerPoint to PDF", desc: "PPT & PPTX",  path: "/dashboard/ppt2pdf",   color: "#c84b1e" },
  { emoji: "🖼️", title: "Image to PDF",      desc: "JPG, PNG...", path: "/dashboard/image2pdf", color: "#7b3fe4" },
  { emoji: "Aa", title: "Text to PDF",       desc: "Turn RichText into a PDF", path: "/dashboard/text2pdf", color: "#0891b2" },
];

const PDF_ADVANCED = [
  { emoji: "🔗", title: "Merge PDF",       desc: "Combine files",       path: "/dashboard/merge",    color: "#6366f1" },
  { emoji: "✂️", title: "Split PDF",       desc: "Extract pages",       path: "/dashboard/split",    color: "#0891b2" },
  { emoji: "↕️", title: "Rearrange PDF",   desc: "Reorder pages",       path: "/dashboard/rearrange",color: "#0891b2" },
  { emoji: "🗜️", title: "Compress PDF",    desc: "Reduce file size",    path: "/dashboard/compress", color: "#1a7a43" },
  { emoji: "🔤", title: "OCR PDF",         desc: "Extract text",        path: "/dashboard/ocr",      color: "#7b3fe4" },
  { emoji: "✍️", title: "E-sign PDF",      desc: "Add signature",       path: "/dashboard/esign",    color: "#c84b1e" },
  { emoji: "🔒", title: "Add Password",    desc: "Protect your PDF",    path: "/dashboard/addpass",  color: "#e5322d" },
  { emoji: "🔓", title: "Remove Password", desc: "Unlock PDF",          path: "/dashboard/removepass",color: "#e5322d" },
];

const QR_TOOLS = [
  { emoji: "↗", title: "Website QR",  desc: "Website URL", path: "/dashboard/qr-link", color: "#0891b2" },
  { emoji: "T", title: "Text QR",  desc: "Rich text", path: "/dashboard/qr-text", color: "#6366f1" },
  { emoji: "⌖", title: "Location QR", desc: "Maps or venue", path: "/dashboard/qr-location", color: "#1a7a43" },
  { emoji: "B", title: "Brand QR", desc: "Logo + website", path: "/dashboard/qr-brand", color: "#e5322d" },
  { emoji: "@", title: "Social QR", desc: "Social profile", path: "/dashboard/qr-social", color: "#7b3fe4" },
  { emoji: "F", title: "File QR", desc: "Uploaded file", path: "/dashboard/qr-file", color: "#c84b1e" },
  { emoji: "ID", title: "vCard QR", desc: "Contact card", path: "/dashboard/qr-vcard", color: "#2b5ce6" },
  { emoji: "#", title: "Product QR", desc: "Product link", path: "/dashboard/qr-product", color: "#d97706" },
  { emoji: "M", title: "Message QR", desc: "Greeting or invite", path: "/dashboard/qr-message", color: "#be185d" },
];

const MY_FILES = [
  { emoji: "📄", title: "Converted PDFs", desc: "View all your PDFs",   path: "/dashboard/pdf-list", color: "#e5322d" },
  { emoji: "📲", title: "My QR Codes",    desc: "Manage your QR codes",  path: "/dashboard/qr-list",  color: "#6366f1" },
];

// ── Sub-components ─────────────────────────────────────────────────
const ToolCard = ({ tool, onClick }) => (
  <div
    className={`dh-card ${tool.comingSoon ? "dh-card-disabled" : ""}`}
    onClick={tool.comingSoon ? undefined : onClick}
  >
    <div className="dh-card-icon" style={{ background: tool.color + "12" }}>
      <span className="dh-card-emoji">{tool.emoji}</span>
    </div>
    <div className="dh-card-body">
      <div className="dh-card-title">
        <span className="dh-card-title-text">{tool.title}</span>
        {tool.comingSoon && <em className="dh-coming-soon">Coming soon</em>}
      </div>
      <div className="dh-card-desc">{tool.desc}</div>
    </div>
    <svg className="dh-card-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
  </div>
);

const Section = ({ eyebrow, title, children }) => (
  <div className="dh-section">
    <div className="dh-section-head">
      <span className="dh-eyebrow">{eyebrow}</span>
      <h3 className="dh-section-title">{title}</h3>
    </div>
    {children}
  </div>
);

// ── Main component ─────────────────────────────────────────────────
const DashboardHome = () => {
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const token    = localStorage.getItem("token") || "";

  const [pdfCount, setPdfCount] = useState(null);
  const [qrCount,  setQrCount]  = useState(null);
  const [recentPdfs, setRecentPdfs] = useState([]);
  const [recentQrs,  setRecentQrs]  = useState([]);

  // Fetch counts + recent items
  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`${process.env.REACT_APP_BASE_URL}/api/Pdf/office-to-pdf`, { headers })
      .then((res) => {
        const data = res.data.data || [];
        setPdfCount(data.length);
        setRecentPdfs(data.slice(0, 3));
      }).catch(() => setPdfCount(0));

    axios.get(`${process.env.REACT_APP_BASE_URL}/api/QR/link-to-qr`, { headers })
      .then((res) => {
        const data = res.data.data || [];
        setQrCount(data.length);
        setRecentQrs(data.slice(0, 3));
      }).catch(() => setQrCount(0));
  }, []);

  const firstName = user.name?.split(" ")[0] || "there";
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";


  return (
    <div className="dh-root">

      {/* ── Greeting banner ── */}
      <div className="dh-banner">
        <div className="dh-banner-text">
          <h2 className="dh-banner-title">{greeting}, {firstName}! 👋</h2>
          <p className="dh-banner-sub">All tools are unlocked. What would you like to do today?</p>
        </div>
        <div className="dh-banner-decoration">
          <span>📄</span><span>🔗</span><span>📊</span>
        </div>
      </div>

      

      {/* ── Convert to PDF ── */}
      <Section eyebrow="PDF Tools" title="Convert to PDF">
        <div className="dh-grid-4">
          {PDF_CONVERT.map((t) => (
            <ToolCard key={t.path || t.title} tool={t} onClick={() => navigate(t.path)} />
          ))}
        </div>
      </Section>

      {/* ── Advanced PDF ── */}
      <Section eyebrow="Advanced PDF" title="Edit, secure & organise">
        <div className="dh-grid-4">
          {PDF_ADVANCED.map((t) => (
            <ToolCard key={t.path} tool={t} onClick={() => navigate(t.path)} />
          ))}
        </div>
      </Section>

      {/* ── QR Tools ── */}
      <Section eyebrow="QR Tools" title="Generate QR Codes">
        <div className="dh-grid-4">
          {QR_TOOLS.map((t) => (
            <ToolCard key={t.path} tool={t} onClick={() => navigate(t.path)} />
          ))}
        </div>
      </Section>

      {/* ── Recent activity + My Files side by side ── */}
      <div className="dh-bottom-row">

        {/* Recent Activity */}
        <div className="dh-recent">
          <div className="dh-section-head">
            <span className="dh-eyebrow">History</span>
            <h3 className="dh-section-title">Recent Activity</h3>
          </div>

          {recentPdfs.length === 0 && recentQrs.length === 0 ? (
            <div className="dh-empty">
              <span className="dh-empty-icon">📂</span>
              <div className="dh-empty-text">No activity yet — convert your first file above!</div>
            </div>
          ) : (
            <div className="dh-activity-list">
              {recentPdfs.map((f, i) => (
                <div key={i} className="dh-activity-row" onClick={() => navigate("/dashboard/pdf-list")}>
                  <div className="dh-act-icon" style={{ background: "#fff0f0" }}>📄</div>
                  <div className="dh-act-body">
                    <div className="dh-act-name">{f.originalFileName || "Converted PDF"}</div>
                    <div className="dh-act-meta">
                      PDF · {f.status === "Converted" ? "✅ Success" : "❌ Failed"}
                    </div>
                  </div>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              ))}
              {recentQrs.map((q, i) => (
                <div key={i} className="dh-activity-row" onClick={() => navigate("/dashboard/qr-list")}>
                  <div className="dh-act-icon" style={{ background: "#f0f0ff" }}>📲</div>
                  <div className="dh-act-body">
                    <div className="dh-act-name">{q.title || "QR Code"}</div>
                    <div className="dh-act-meta">QR Code · {q.link ? "Link" : "Text"}</div>
                  </div>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Files quick access */}
        <div className="dh-myfiles">
          <div className="dh-section-head">
            <span className="dh-eyebrow">My Files</span>
            <h3 className="dh-section-title">Quick Access</h3>
          </div>
          <div className="dh-myfiles-cards">
            {MY_FILES.map((t) => (
              <div key={t.path} className="dh-myfile-card" onClick={() => navigate(t.path)}>
                <div className="dh-myfile-icon" style={{ background: t.color + "12" }}>
                  <span style={{ fontSize: 24 }}>{t.emoji}</span>
                </div>
                <div className="dh-myfile-body">
                  <div className="dh-myfile-title">{t.title}</div>
                  <div className="dh-myfile-count" style={{ color: t.color }}>
                    {t.path.includes("pdf") ? (pdfCount === null ? "—" : `${pdfCount} files`) : (qrCount === null ? "—" : `${qrCount} codes`)}
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardHome;
